import { PROJECT_OPS_CENTER_CODE, PROJECT_OPS_CENTER_NAME } from '../config/hrConstants';

/** 员工可选归属：中心(2)、项目/子项目(3) */
const SELECTABLE_ORG_TYPES = new Set([2, 3]);

function normalizeOrgCode(code) {
  if (code == null || code === '') return '';
  return String(code).trim().split('-').map((part) => {
    if (/^\d+$/.test(part)) return String(parseInt(part, 10));
    return part;
  }).join('-');
}

function orgCodesEqual(a, b) {
  if (a == null || b == null) return false;
  return normalizeOrgCode(a) === normalizeOrgCode(b);
}

function resolveOpsCenterCode(orgs) {
  const hit = orgs.find(
    (o) => o.org_type === 2
      && (o.org_name === PROJECT_OPS_CENTER_NAME
        || o.org_name === '项目运营中心'
        || orgCodesEqual(o.org_code, PROJECT_OPS_CENTER_CODE))
  );
  return hit?.org_code || PROJECT_OPS_CENTER_CODE;
}

function sortOrgNodes(a, b) {
  const sortDiff = (a.sort || 0) - (b.sort || 0);
  if (sortDiff !== 0) return sortDiff;
  const codeA = a.org_code ? parseInt(String(a.org_code).split('-')[0], 10) : 9999;
  const codeB = b.org_code ? parseInt(String(b.org_code).split('-')[0], 10) : 9999;
  if (codeA !== codeB) return codeA - codeB;
  return String(a.org_code).localeCompare(String(b.org_code));
}

export function buildOrgTreeFromFlat(items, parentOrgCode = null) {
  return items
    .filter((item) => (item.parent_org_code || null) === (parentOrgCode || null))
    .map((item) => {
      const children = buildOrgTreeFromFlat(items, item.org_code);
      return {
        ...item,
        children: children.length ? children : undefined
      };
    })
    .sort(sortOrgNodes);
}

function getOrgTypeLabel(org, opsCenterCode, orgMap) {
  if (org.org_type === 2) return '职能';
  if (org.org_type === 3) {
    if (orgCodesEqual(org.parent_org_code, opsCenterCode)) return '项目';
    const parent = orgMap.get(org.parent_org_code);
    if (parent?.org_type === 3 && orgCodesEqual(parent.parent_org_code, opsCenterCode)) {
      return '分区项目';
    }
    return '子部门';
  }
  if (org.org_type === 4) return '项目部门';
  return '集团';
}

function resolveSelectableOrgTypes(options = {}) {
  if (options.selectableOrgTypes) return options.selectableOrgTypes;
  return SELECTABLE_ORG_TYPES;
}

function isOrgNodeSelectable(org, orgMap, options = {}) {
  const selectableTypes = resolveSelectableOrgTypes(options);
  if (!selectableTypes.has(org.org_type)) return false;
  if (!options.respectSelectableFlag) return true;
  const source = orgMap.get(org.org_code) || org;
  return source.selectable !== false;
}

function toTreeSelectNode(org, opsCenterCode, orgMap, options = {}) {
  const typeLabel = getOrgTypeLabel(org, opsCenterCode, orgMap);
  return {
    value: org.org_code,
    key: org.org_code,
    title: `[${typeLabel}] ${org.org_name}`,
    typeLabel,
    disabled: !isOrgNodeSelectable(org, orgMap, options),
    children: org.children?.map((child) => toTreeSelectNode(child, opsCenterCode, orgMap, options))
  };
}

/** 将扁平组织列表转为员工「所在部门/项目」TreeSelect 数据 */
export function buildEmployeeDeptTreeData(orgs, opsCenterCode = null, options = {}) {
  if (!orgs?.length) return [];
  const resolvedOpsCode = opsCenterCode || resolveOpsCenterCode(orgs);
  const orgMap = new Map(orgs.map((o) => [o.org_code, o]));
  const tree = buildOrgTreeFromFlat(orgs);
  return tree.map((node) => toTreeSelectNode(node, resolvedOpsCode, orgMap, options));
}

/** 预算归属：仅职能中心 + 项目运营中心直属项目（树中不展示集团/部门/子项目/项目部门） */
const BUDGET_ANCHOR_TYPE_LABELS = new Set(['中心', '项目']);

function isDirectOpsCenterProject(org, opsCenterCode) {
  return org.org_type === 3 && orgCodesEqual(org.parent_org_code, opsCenterCode);
}

/** 预算组织树：顶层为各职能中心；项目运营中心下挂直属项目，其余节点不展开子级。
 *  options.includeSubProjects=true 时，项目下再展开「分区项目」（仅用于特定单据，如社保购买/减少）。 */
export function buildBudgetOrgTreeData(orgs, opsCenterCode = null, options = {}) {
  if (!orgs?.length) return [];
  const resolvedOpsCode = opsCenterCode || resolveOpsCenterCode(orgs);
  const centers = orgs.filter((o) => o.org_type === 2).sort(sortOrgNodes);
  const directProjects = orgs
    .filter((o) => isDirectOpsCenterProject(o, resolvedOpsCode))
    .sort(sortOrgNodes);

  const buildProjectNode = (project) => {
    const node = {
      value: project.org_code,
      key: project.org_code,
      title: project.org_name,
      typeLabel: '项目'
    };
    if (options.includeSubProjects) {
      const subProjects = orgs
        .filter((o) => o.org_type === 3 && orgCodesEqual(o.parent_org_code, project.org_code))
        .sort(sortOrgNodes);
      if (subProjects.length) {
        node.children = subProjects.map((sub) => ({
          value: sub.org_code,
          key: sub.org_code,
          title: sub.org_name,
          typeLabel: '分区项目'
        }));
      }
    }
    return node;
  };

  return centers.map((center) => {
    const isOpsCenter = orgCodesEqual(center.org_code, resolvedOpsCode);
    const projectNodes = isOpsCenter ? directProjects.map(buildProjectNode) : [];
    return {
      value: center.org_code,
      key: center.org_code,
      title: center.org_name,
      typeLabel: '中心',
      ...(projectNodes.length ? { children: projectNodes } : {})
    };
  });
}

export function isBudgetAnchorOrgLabel(typeLabel) {
  return BUDGET_ANCHOR_TYPE_LABELS.has(typeLabel);
}

/** 预算看板/调整申请：组织 TreeSelect 通用 props。
 *  overrides.includeSubProjects=true 时项目下展开「分区项目」。 */
export function buildBudgetOrgTreeSelectProps(orgs, overrides = {}) {
  const { includeSubProjects, ...rest } = overrides;
  const treeData = buildBudgetOrgTreeData(orgs, null, { includeSubProjects });
  return {
    treeData,
    showSearch: true,
    treeNodeFilterProp: 'title',
    placeholder: '选择职能中心或项目',
    allowClear: true,
    treeDefaultExpandedKeys: collectDefaultExpandedOrgTreeKeys(treeData),
    ...rest
  };
}

/** 员工/计划/考勤/物资：中心+项目组织 TreeSelect 通用 props */
export function buildEmployeeDeptTreeSelectProps(orgs, overrides = {}) {
  const {
    multiple = false,
    placeholder,
    style,
    respectSelectableFlag = false,
    selectableOrgTypes,
    ...rest
  } = overrides;
  const treeData = buildEmployeeDeptTreeData(orgs, null, { respectSelectableFlag, selectableOrgTypes });
  return {
    treeData,
    multiple,
    showSearch: true,
    treeNodeFilterProp: 'title',
    placeholder: placeholder ?? (multiple ? '按组织架构选择中心或项目' : '选择职能中心或项目'),
    allowClear: true,
    treeDefaultExpandedKeys: collectDefaultExpandedOrgTreeKeys(treeData),
    ...(multiple ? { maxTagCount: 'responsive' } : {}),
    style: style ?? { width: '100%' },
    ...rest
  };
}

const COLLAPSED_DEFAULT_TYPE_LABELS = new Set(['分区项目', '子部门', '项目部门']);

/** 子项目、子部门、项目部门默认收起 */
export function isOrgNodeCollapsedByDefault(node) {
  if (node?.typeLabel && COLLAPSED_DEFAULT_TYPE_LABELS.has(node.typeLabel)) return true;
  const title = String(node?.title || '');
  return ['[分区项目]', '[子部门]', '[项目部门]'].some((prefix) => title.startsWith(prefix));
}

/** 组织树默认展开的节点 key（含子项目/子部门的父节点保持收起） */
export function collectDefaultExpandedOrgTreeKeys(nodes) {
  const keys = [];
  const walk = (list) => {
    (list || []).forEach((node) => {
      if (isOrgNodeCollapsedByDefault(node)) return;

      const children = node.children || [];
      const hasDefaultCollapsedChild = children.some(isOrgNodeCollapsedByDefault);
      const key = node.key ?? node.value ?? node.org_code;

      if (key != null && !hasDefaultCollapsedChild) {
        keys.push(String(key));
        walk(children);
      }
    });
  };
  walk(nodes);
  return keys;
}

export function collectAllOrgTreeKeys(nodes) {
  const keys = [];
  const walk = (list) => {
    (list || []).forEach((node) => {
      const key = node.key ?? node.value ?? node.org_code;
      if (key != null) keys.push(String(key));
      if (node.children?.length) walk(node.children);
    });
  };
  walk(nodes);
  return keys;
}

export { orgCodesEqual, normalizeOrgCode, resolveOpsCenterCode };
